import { parseDbInstant, toTenantDateStr } from '../date/tenantDate';

/**
 * Pure reduction helpers for the BULK lifecycle period bundle (the review-modal
 * preview of which jobs will stage into opening/closing invoices across N
 * customers). Kept supabase-free so the window/billed logic is unit-testable
 * without mocking the query builder.
 *
 * MIRROR FOOTGUN: this reconstructs `stage_lifecycle_invoice`'s job selection
 * (migration 20260504120300), the same logic `fetchLifecyclePeriodBundle`
 * mirrors for the single-customer gate. If that trigger's eligibility changes,
 * change this too — there is no auto-sync.
 *
 * window_start per customer = the TENANT-TZ date portion of the latest
 * `customer_status_changes.changed_at` (a `timestamptz`, always carries an
 * offset) whose `to_status = context`, falling back to `customers.created_at`
 * (offset-less UTC — parsed via `parseDbInstant` first, per CLAUDE.md's
 * "offset-less DB timestamps" rule) for customers with no such row. A job is
 * eligible when its `date` is in `[window_start, transitionDate]`, its
 * `job_context = context`, and it is NOT already on a live (draft, sending,
 * sent, or paid — not soft-deleted) invoice snapshot.
 */

/**
 * Resolve window_start ('YYYY-MM-DD', in the TENANT's calendar) per customer.
 *
 * @param {{
 *   customerIds: string[],
 *   changeRows: Array<{ customer_id: string, changed_at: string }>,  // DESC by changed_at, timestamptz
 *   customerCreatedRows: Array<{ id: string, created_at: string }>,  // fallback, offset-less UTC
 *   tz?: string,  // IANA timezone; defaults to UTC (matches the prior UTC-slice behavior)
 * }} params
 * @returns {Map<string, string>} customerId → window_start (omits ids with no resolvable window)
 */
export function resolveBulkWindowStarts({ customerIds, changeRows, customerCreatedRows, tz = 'UTC' }) {
  const windowStart = new Map();

  // changeRows arrive DESC, so the first row seen per customer is the latest.
  for (const r of changeRows || []) {
    const cid = String(r.customer_id);
    if (!windowStart.has(cid) && r?.changed_at) {
      windowStart.set(cid, toTenantDateStr(r.changed_at, tz));
    }
  }

  // Fallback to created_at for any requested customer still without a window.
  // created_at is offset-less UTC (a legacy `timestamp without time zone`
  // column) — parseDbInstant anchors it to UTC before re-projecting into tz,
  // exactly as toTenantDateStr does for the timestamptz change rows above.
  const createdById = new Map();
  for (const c of customerCreatedRows || []) {
    if (c?.created_at) createdById.set(String(c.id), toTenantDateStr(parseDbInstant(c.created_at), tz));
  }
  for (const id of customerIds || []) {
    const cid = String(id);
    if (!windowStart.has(cid) && createdById.has(cid)) {
      windowStart.set(cid, createdById.get(cid));
    }
  }

  return windowStart;
}

/** Extract billed job ids from a single invoice snapshot (object or JSON string). */
function snapshotJobIds(snapshot) {
  let snap = snapshot;
  if (!snap) return [];
  if (typeof snap === 'string') {
    try { snap = JSON.parse(snap); } catch { return []; }
  }
  const jobs = Array.isArray(snap?.jobs) ? snap.jobs : [];
  return jobs.filter((j) => j?.id != null).map((j) => String(j.id));
}

/**
 * Per-customer sets of job ids billed on sent/paid invoices (snapshot.jobs[].id).
 * Mirrors the billed exclusion in assembleBulkLifecycleBundles.
 *
 * @param {Array<{ customer_id: string, snapshot?: unknown }>} invoiceRows sent/paid only
 * @returns {Map<string, Set<string>>}
 */
export function buildSentPaidBilledJobIdsByCustomer(invoiceRows = []) {
  const billedByCustomer = new Map();
  for (const inv of invoiceRows) {
    const cid = String(inv.customer_id);
    if (!billedByCustomer.has(cid)) billedByCustomer.set(cid, new Set());
    const set = billedByCustomer.get(cid);
    for (const jid of snapshotJobIds(inv?.snapshot)) set.add(jid);
  }
  return billedByCustomer;
}

/**
 * Reduce raw query rows into a per-customer bundle map. Floors each job by its
 * customer's window_start, caps at transitionDate, drops billed jobs, and
 * attaches line items.
 *
 * @param {{
 *   customerIds: string[],
 *   windowStartByCustomer: Map<string, string>,
 *   jobRows: Array<{ id, customer_id, date, total, unpriced_line_count }>,
 *   invoiceRows: Array<{ customer_id, snapshot }>,   // LIVE (draft/sending/sent/paid, not deleted) only
 *   lineRows: Array<{ job_id, type, name, unit_price, quantity, catalog_id }>,
 *   transitionDate: string,  // 'YYYY-MM-DD' upper bound
 *   billedExclusionUnknown?: boolean,  // GROUNDWORK ONLY (RD-ROLE-005b B4,
 *     2026-09-18) — stamped verbatim onto every bundle, true when the caller
 *     could not VERIFY nothing is billed (no `invoices` SELECT, e.g. a field
 *     role — or an unresolved tenant role). No consumer reads this field
 *     today. Field roles' `billedJobIds` is an EMPTY array BY CONSTRUCTION
 *     (they hold zero `invoices` SELECT — the query is skipped upstream, not
 *     run-and-filtered), so no future consumer may treat an empty
 *     `billedJobIds` as a verified "nothing billed" without first checking
 *     this flag.
 *   windowUnknown?: boolean,  // FIXED CONTRACT (RD-ROLE-005b B1) — always
 *     `false` here. The ONLY producer of `true` is the operations
 *     short-circuit in lifecycleBundleServiceBulk.js, which returns before
 *     ever calling this function. Present on every bundle so a consumer
 *     never has to distinguish `false` from `undefined`.
 * }} params
 * @returns {Map<string, { jobCount, total: number|null, jobs: Array, windowStart: string|null, windowUnknown: boolean, billedJobIds: string[], billedExclusionUnknown: boolean }>}
 *   Every requested id is present; customers with no eligible jobs map to
 *   `total: 0` (a real, confirmed answer — nothing to bill), never `null`.
 *   `total` is `null` only when at least one surviving job's own total is
 *   unknown (blind branch) — never a fabricated partial sum, never `NaN`.
 */
export function assembleBulkLifecycleBundles({
  customerIds,
  windowStartByCustomer,
  jobRows,
  invoiceRows,
  lineRows,
  transitionDate,
  billedExclusionUnknown = false,
  windowUnknown = false,
}) {
  // Billed exclusion set per customer — built first so EVERY entry (including
  // customers whose jobs are all billed → empty bundle) can expose its billed
  // ids. The route board's instant in-memory count reuses this set as the single
  // source of billed-exclusion truth, so the card count can never over-count a
  // job the authoritative bundle excluded.
  const billedByCustomer = new Map();
  for (const inv of invoiceRows || []) {
    const cid = String(inv.customer_id);
    if (!billedByCustomer.has(cid)) billedByCustomer.set(cid, new Set());
    const set = billedByCustomer.get(cid);
    for (const jid of snapshotJobIds(inv?.snapshot)) set.add(jid);
  }

  const result = new Map();
  for (const id of customerIds || []) {
    const cid = String(id);
    result.set(cid, {
      jobCount: 0,
      total: 0,
      jobs: [],
      windowStart: windowStartByCustomer?.get(cid) ?? null,
      windowUnknown,
      billedJobIds: [...(billedByCustomer.get(cid) || [])],
      billedExclusionUnknown,
    });
  }

  // Eligible jobs: floored by per-customer window, capped at transitionDate.
  const eligibleByCustomer = new Map();
  for (const j of jobRows || []) {
    const cid = String(j.customer_id);
    const ws = windowStartByCustomer?.get(cid);
    if (!ws) continue;
    if (j.date < ws) continue;
    if (transitionDate && j.date > transitionDate) continue;
    if (!eligibleByCustomer.has(cid)) eligibleByCustomer.set(cid, []);
    eligibleByCustomer.get(cid).push(j);
  }

  // Line items grouped by job.
  const linesByJobId = new Map();
  for (const line of lineRows || []) {
    if (!line?.job_id) continue;
    const k = String(line.job_id);
    if (!linesByJobId.has(k)) linesByJobId.set(k, []);
    linesByJobId.get(k).push({
      type: line.type ?? null,
      name: line.name ?? null,
      // NEVER coerce a NULL unit_price to 0 — null means "office prices it"
      // (RD-ROLE-005); 0 means a real, deliberate free line. Money doctrine:
      // absence and zero are different answers (CLAUDE.md).
      unit_price: line.unit_price ?? null,
      quantity: line.quantity ?? 1,
      catalog_id: line.catalog_id ?? null,
    });
  }

  for (const [cid, jobs] of eligibleByCustomer.entries()) {
    const billed = billedByCustomer.get(cid) || new Set();
    const surviving = jobs.filter((j) => !billed.has(String(j.id)));
    if (surviving.length === 0) continue;
    const built = surviving.map((j) => ({
      id: String(j.id),
      date: j.date,
      // NEVER coerce to 0 — `total`/`unpriced_line_count` are `undefined` on
      // a blind bundle (field_jobs carries neither column — see
      // fetchBulkLifecyclePeriodBundles); that is a genuinely UNKNOWN value,
      // not a $0 visit. Money doctrine: absence and zero are different
      // answers (CLAUDE.md; B2, RD-ROLE-005b 2026-09-18).
      total: j.total ?? null,
      unpriced_line_count: j.unpriced_line_count ?? null,
      lines: linesByJobId.get(String(j.id)) ?? [],
    }));
    // The per-customer aggregate must not present a partial sum as the real
    // total: if ANY surviving job's total is unknown, the aggregate is
    // unknown too — never a sum that silently treated a blind job as $0,
    // and never NaN (a `null` operand added would otherwise coerce to 0
    // rather than propagate, which is exactly the bug this guards against).
    const anyUnknownTotal = built.some((j) => j.total === null);
    const total = anyUnknownTotal ? null : built.reduce((sum, j) => sum + j.total, 0);
    result.set(cid, {
      jobCount: built.length,
      total,
      jobs: built,
      windowStart: windowStartByCustomer?.get(cid) ?? null,
      windowUnknown,
      billedJobIds: [...billed],
      billedExclusionUnknown,
    });
  }

  return result;
}
